import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import * as PropTypes from 'prop-types';
import cn from 'classnames';
import { Modal } from './Modal';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useBlockScroll } from '../../hooks/useBlockScroll';

import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';

const ModalResponse = ({
  allowScroll,
  shouldScroll,
  isOpen,
  onClickOverlay,
  onClickButton,
  children,
  isSuccess,
  successIcon,
  failIcon,
  successTitle,
  failTitle,
  successText,
  failText,
}) => {
  if (!(allowScroll ?? false)) {
    useBlockScroll(isOpen, shouldScroll);
  }
  const ref = useRef(null);
  const iconSuccess = successIcon || (
    <BetaIcon
      name={'circleCheck'}
      color={IconColors.regular}
      size={IconSizes[32]}
    />
  );
  const iconFail = failIcon || (
    <BetaIcon
      name={'warning'}
      color={IconColors.regular}
      size={IconSizes[32]}
    />
  );

  return createPortal(
    <CSSTransition
      classNames="modal-container"
      in={isOpen}
      mountOnEnter={true}
      unmountOnExit={true}
      appear={true}
      timeout={200}
    >
      <div
        key="modal"
        className={cn('modal-container')}
        ref={ref}
        onMouseDown={(e) => ref.current === e.target && onClickOverlay()}
      >
        <div className={cn('grid-container', 'fluid', 'modal')}>
          <div className="modal-response">
            <div className="modal-response__header">
              <div className="modal-response__header__close-btn">
                <button className="pointer" onClick={() => onClickButton()}>
                  <BetaIcon
                    name={'close'}
                    size={IconSizes[12]}
                    color={IconColors.regular}
                  />
                </button>
              </div>
              <Typography
                variant={TypographyVariants.h6}
                weight={TypographyWeights['semi-bold']}
              >
                {isSuccess ? successTitle : failTitle}
              </Typography>
            </div>
            <div className="modal-response__body">
              <div className="d-flex flex-column items-center pb-xl gap-xs">
                {isSuccess ? iconSuccess : iconFail}
                {isSuccess ? successText : failText}
              </div>
              <div className="d-flex align-center gap-xs">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </CSSTransition>,
    document.body
  );
};

ModalResponse.propTypes = {
  shouldScroll: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClickButton: PropTypes.func.isRequired,
  onClickOverlay: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isSuccess: PropTypes.bool.isRequired,
};

export { ModalResponse };
